import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStrings } from '../../i18n';
import { adminAPI } from '../../services/adminService';
import PanchTatvaButton from '../../components/PanchTatvaButton';
import { CartToastContainer, showCartToast } from '../../components/CartToast';

export default function AdminProducts() {
  const strings = useStrings();
  const { getIdToken } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    element: 'earth',
    price: '',
    costPrice: '',
    quantity: '',
    image: '',
    warehouseLocation: ''
  });

  const ELEMENTS = ['earth', 'water', 'fire', 'air', 'ether'];

  // Refactor fetchProducts to useCallback and add it to useEffect dependencies
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const idToken = await getIdToken();

      const params = new URLSearchParams({
        page,
        limit: 10,
        search: searchTerm,
        status: statusFilter !== 'all' ? statusFilter : ''
      });

      const response = await adminAPI.products.list(params, idToken);

      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }

      const data = await response.json();
      setProducts(data.products);
      setTotalPages(data.pagination.pages);
    } catch (err) {
      setError(err.message);
      showCartToast(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [getIdToken, page, searchTerm, statusFilter]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      const idToken = await getIdToken();
      
      const productData = {
        name: formData.name,
        description: formData.description,
        element: formData.element,
        price: parseFloat(formData.price),
        costPrice: parseFloat(formData.costPrice),
        image: formData.image,
        inventory: {
          quantity: parseInt(formData.quantity),
          warehouseLocation: formData.warehouseLocation
        }
      };

      const response = await adminAPI.products.create(productData, idToken);
      
      if (!response.ok) {
        throw new Error('Failed to create product');
      }

      showCartToast('Product created successfully!');
      setShowAddForm(false);
      setFormData({
        name: '',
        description: '',
        element: 'earth',
        price: '',
        costPrice: '',
        quantity: '',
        image: '',
        warehouseLocation: ''
      });
      fetchProducts();
    } catch (err) {
      showCartToast(`Error: ${err.message}`);
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      const idToken = await getIdToken();
      
      const updateData = {
        name: formData.name,
        description: formData.description,
        element: formData.element,
        price: parseFloat(formData.price),
        costPrice: parseFloat(formData.costPrice),
        image: formData.image,
        inventory: {
          quantity: parseInt(formData.quantity),
          warehouseLocation: formData.warehouseLocation
        }
      };

      const response = await adminAPI.products.update(editingProduct._id, updateData, idToken);
      
      if (!response.ok) {
        throw new Error('Failed to update product');
      }

      showCartToast('Product updated successfully!');
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        element: 'earth',
        price: '',
        costPrice: '',
        quantity: '',
        image: '',
        warehouseLocation: ''
      });
      fetchProducts();
    } catch (err) {
      showCartToast(`Error: ${err.message}`);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const idToken = await getIdToken();
      const response = await adminAPI.products.delete(productId, idToken);
      
      if (!response.ok) {
        throw new Error('Failed to delete product');
      }

      showCartToast('Product deleted successfully!');
      fetchProducts();
    } catch (err) {
      showCartToast(`Error: ${err.message}`);
    }
  };

  const handleEditClick = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      element: product.element,
      price: product.price,
      costPrice: product.costPrice,
      quantity: product.inventory.quantity,
      image: product.image,
      warehouseLocation: product.inventory.warehouseLocation
    });
    setShowAddForm(true);
  };

  const handleCancelForm = () => {
    setShowAddForm(false);
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      element: 'earth',
      price: '',
      costPrice: '',
      quantity: '',
      image: '',
      warehouseLocation: ''
    });
  };

  return (
    <div className="w-full bg-primaryBg min-h-screen">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20 fade-section show">
        {/* Back Button */}
        <Link to="/admin" className="inline-flex items-center text-secondary hover:text-secondary/80 mb-6 transition-colors">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {strings.admin?.backToDashboard || 'Back to Dashboard'}
        </Link>

        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-heading text-headingText mb-2">
              {strings.admin?.products || 'Products'}
            </h1>
            <p className="text-bodyText">
              {strings.admin?.manageYourInventory || 'Manage your product inventory'}
            </p>
          </div>
          <PanchTatvaButton 
            variant="primary" 
            size="large"
            onClick={() => {
              setEditingProduct(null);
              setShowAddForm(!showAddForm);
            }}
            className="mt-4 md:mt-0"
          >
            {showAddForm ? '✕ Close' : '+ Add Product'}
          </PanchTatvaButton>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="bg-cardBg rounded-2xl shadow-card p-8 border border-secondary/10 mb-8">
            <h2 className="text-2xl font-heading text-headingText mb-6">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h2>
            <form onSubmit={editingProduct ? handleUpdateProduct : handleAddProduct} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-headingText mb-2">
                    {strings.admin?.productName || 'Product Name'} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText placeholder-textSecondary focus:outline-none focus:border-secondary"
                    placeholder="e.g., Earth Soap"
                  />
                </div>

                {/* Element */}
                <div>
                  <label className="block text-sm font-medium text-headingText mb-2">
                    {strings.admin?.element || 'Element'} *
                  </label>
                  <select
                    required
                    value={formData.element}
                    onChange={(e) => setFormData({ ...formData, element: e.target.value })}
                    className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText focus:outline-none focus:border-secondary"
                  >
                    {ELEMENTS.map(el => (
                      <option key={el} value={el}>{el.charAt(0).toUpperCase() + el.slice(1)}</option>
                    ))}
                  </select>
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-headingText mb-2">
                    {strings.admin?.price || 'Price'} (₹) *
                  </label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText placeholder-textSecondary focus:outline-none focus:border-secondary"
                    placeholder="50"
                  />
                </div>

                {/* Cost Price */}
                <div>
                  <label className="block text-sm font-medium text-headingText mb-2">
                    {strings.admin?.costPrice || 'Cost Price'} (₹) *
                  </label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    min="0"
                    value={formData.costPrice}
                    onChange={(e) => setFormData({ ...formData, costPrice: e.target.value })}
                    className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText placeholder-textSecondary focus:outline-none focus:border-secondary"
                    placeholder="20"
                  />
                </div>

                {/* Quantity */}
                <div>
                  <label className="block text-sm font-medium text-headingText mb-2">
                    {strings.admin?.quantity || 'Quantity'} *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText placeholder-textSecondary focus:outline-none focus:border-secondary"
                    placeholder="100"
                  />
                </div>

                {/* Warehouse Location */}
                <div>
                  <label className="block text-sm font-medium text-headingText mb-2">
                    {strings.admin?.warehouseLocation || 'Warehouse Location'} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.warehouseLocation}
                    onChange={(e) => setFormData({ ...formData, warehouseLocation: e.target.value })}
                    className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText placeholder-textSecondary focus:outline-none focus:border-secondary"
                    placeholder="e.g., A1-B2"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-headingText mb-2">
                  {strings.admin?.description || 'Description'}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText placeholder-textSecondary focus:outline-none focus:border-secondary"
                  placeholder="Product description"
                  rows="4"
                />
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-headingText mb-2">
                  {strings.admin?.imageUrl || 'Image URL'}
                </label>
                <input
                  type="url"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText placeholder-textSecondary focus:outline-none focus:border-secondary"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-4 pt-4">
                <PanchTatvaButton variant="primary" size="large" type="submit">
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </PanchTatvaButton>
                <PanchTatvaButton variant="outline" size="large" onClick={handleCancelForm} type="button">
                  {strings.admin?.cancel || 'Cancel'}
                </PanchTatvaButton>
              </div>
            </form>
          </div>
        )}

        {/* Filters */}
        <div className="mb-8 flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder={strings.admin?.searchProducts || 'Search products...'}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText placeholder-textSecondary focus:outline-none focus:border-secondary"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText focus:outline-none focus:border-secondary"
          >
            <option value="all">{strings.admin?.allStatus || 'All Status'}</option>
            <option value="active">{strings.admin?.active || 'Active'}</option>
            <option value="discontinued">{strings.admin?.discontinued || 'Discontinued'}</option>
            <option value="draft">{strings.admin?.draft || 'Draft'}</option>
          </select>
        </div>

        {/* Products Table */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-bodyText">{strings.admin?.loading || 'Loading...'}</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            {error}
          </div>
        ) : products.length === 0 ? (
          <div className="bg-cardBg rounded-2xl shadow-card p-12 text-center border border-secondary/10">
            <p className="text-bodyText text-lg">{strings.admin?.noProducts || 'No products found'}</p>
          </div>
        ) : (
          <div className="bg-cardBg rounded-2xl shadow-card overflow-hidden border border-secondary/10">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface border-b border-secondary/20">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.name || 'Name'}</th>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.element || 'Element'}</th>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.price || 'Price'}</th>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.quantity || 'Quantity'}</th>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.profit || 'Margin'}</th>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.actions || 'Actions'}</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => {
                    const margin = ((product.price - product.costPrice) / product.price * 100).toFixed(1);
                    return (
                      <tr key={product._id} className="border-b border-secondary/10 hover:bg-surface transition-colors duration-200">
                        <td className="px-6 py-4 text-bodyText">{product.name}</td>
                        <td className="px-6 py-4 text-bodyText capitalize">{product.element}</td>
                        <td className="px-6 py-4 text-bodyText">₹{product.price}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${
                            product.inventory.quantity > 50 ? 'bg-green-600' :
                            product.inventory.quantity > 20 ? 'bg-yellow-600' :
                            'bg-red-600'
                          }`}>
                            {product.inventory.quantity}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-bodyText">{margin}%</td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditClick(product)}
                              className="px-3 py-1 text-sm text-secondary hover:bg-secondary/10 rounded-lg transition-colors duration-200"
                            >
                              {strings.admin?.edit || 'Edit'}
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(product._id)}
                              className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                            >
                              {strings.admin?.delete || 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 bg-surface border-t border-secondary/20 flex items-center justify-center gap-4">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm text-bodyText hover:text-secondary disabled:opacity-50"
                >
                  ← {strings.admin?.previous || 'Previous'}
                </button>
                <span className="text-sm text-bodyText">
                  {strings.admin?.page || 'Page'} {page} {strings.admin?.of || 'of'} {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm text-bodyText hover:text-secondary disabled:opacity-50"
                >
                  {strings.admin?.next || 'Next'} →
                </button>
              </div>
            )}
          </div>
        )}

      <CartToastContainer />
      </div>
    </div>
  );
}
